import { query } from 'bitecs';
import { Config } from '@src/core/Config';
import { GameEvent } from '@src/core/EventQueue';
import { Health, Player, Position, Spawner, Sprite, Velocity, Weapon } from '@src/ecs/components';
import type { GameSystem } from '@src/ecs/System';
import { emitParticles, ParticleKind, RoomPhase, type GameContext } from '@src/ecs/world';
import { MAX_PLAYERS, type PlayerInput } from '@src/input/InputState';
import { TouchControls } from '@src/input/TouchControls';

const DIR_RIGHT = 1;
const DIR_LEFT = 2;
const DIR_UP = 4;
const DIR_DOWN = 8;

export type PointerToWorld = (screenX: number, screenY: number, out: Float32Array) => void;

const AIM_MAGNET_COS = Math.cos((Config.player.aimMagnetismDeg * Math.PI) / 180);
/** Seconds after the last mouse motion during which a pointer still counts as active aiming. */
const POINTER_AIM_HOLD = 0.4;

/**
 * Reads keyboard/mouse, touch areas, and gamepads into PlayerInput snapshots, then applies the
 * control-physics rules (deadzone, squared curve, accel/slide, backpedal penalty,
 * spawner aim magnetism) to every Player entity.
 */
export class InputSystem implements GameSystem {
  private ctx!: GameContext;
  private readonly keys = new Set<string>();
  private mouseX = 0;
  private mouseY = 0;
  private mouseDown = false;
  private mouseMoved = false;
  /** Seconds since the pointer last moved; starts "stale" so a parked mouse never steers facing. */
  private pointerAge = POINTER_AIM_HOLD;
  private readonly pointerWorld = new Float32Array(2);
  private pointerToWorld: PointerToWorld = (_x, _y, out) => {
    out[0] = 0;
    out[1] = 0;
  };
  private readonly target: HTMLElement;
  public touchControls!: TouchControls;
  private readonly lastDigital = new Uint8Array(MAX_PLAYERS);
  private readonly lastTapDir = new Uint8Array(MAX_PLAYERS);
  private readonly lastTapAge = new Float32Array(MAX_PLAYERS);
  private readonly dashCooldown = new Float32Array(MAX_PLAYERS);

  constructor(target: HTMLElement) {
    this.target = target;
  }

  setPointerMapper(fn: PointerToWorld): void {
    this.pointerToWorld = fn;
  }

  setTouchControlsVisible(visible: boolean): void {
    this.touchControls?.setVisible(visible);
  }

  init(ctx: GameContext): void {
    this.ctx = ctx;
    this.touchControls = new TouchControls(document.body);
    window.addEventListener('keydown', this.onKeyDown);
    window.addEventListener('keyup', this.onKeyUp);
    window.addEventListener('blur', this.onBlur);
    // Listen on window so mouse still aims/fires when a touch-zone overlay is
    // above the canvas (hybrid touchscreen + mouse devices).
    window.addEventListener('mousemove', this.onMouseMove);
    window.addEventListener('mousedown', this.onMouseDown);
    window.addEventListener('mouseup', this.onMouseUp);
    this.target.addEventListener('contextmenu', this.onContextMenu);
  }

  destroy(): void {
    this.touchControls?.destroy();
    window.removeEventListener('keydown', this.onKeyDown);
    window.removeEventListener('keyup', this.onKeyUp);
    window.removeEventListener('blur', this.onBlur);
    window.removeEventListener('mousemove', this.onMouseMove);
    window.removeEventListener('mousedown', this.onMouseDown);
    window.removeEventListener('mouseup', this.onMouseUp);
    this.target.removeEventListener('contextmenu', this.onContextMenu);
  }

  isKeyDown(code: string): boolean {
    return this.keys.has(code);
  }

  update(dt: number): void {
    this.pointerAge = Math.min(POINTER_AIM_HOLD, this.pointerAge + dt);
    this.pollDevices();
    const ctx = this.ctx;
    const players = query(ctx.world, [Player, Position, Velocity, Weapon]);
    for (let i = 0; i < players.length; i++) {
      const eid = players[i];
      if (Player.dead[eid] || ctx.room.phase === RoomPhase.Victory || ctx.room.phase === RoomPhase.GameOver) {
        Velocity.x[eid] = 0;
        Velocity.y[eid] = 0;
        Player.firing[eid] = 0;
        Player.dashTime[eid] = 0;
        const deadSlot = Player.inputIndex[eid];
        this.lastDigital[deadSlot] = 0;
        this.lastTapDir[deadSlot] = 0;
        this.lastTapAge[deadSlot] = 99;
        continue;
      }
      const input = ctx.inputs[Player.inputIndex[eid]];
      this.applyMovement(eid, input, dt);
      this.applyAim(eid, input);
      Player.firing[eid] = input.fire ? 1 : 0;
      Player.aiming[eid] = input.aiming ? 1 : 0;
    }
  }

  private pollDevices(): void {
    const inputs = this.ctx.inputs;
    const pads = typeof navigator.getGamepads === 'function' ? navigator.getGamepads() : [];

    for (let p = 0; p < MAX_PLAYERS; p++) {
      const input = inputs[p];
      const pad = pads[p] ?? null;
      input.connected = p === 0 || pad !== null;
      let mx = 0;
      let my = 0;
      let ax = 0;
      let ay = 0;
      let fire = false;
      let aimIsPointer = false;
      let aiming = false;

      if (pad) {
        mx = pad.axes[0] ?? 0;
        my = -(pad.axes[1] ?? 0);
        const dpadX = (pad.buttons[15]?.pressed ? 1 : 0) - (pad.buttons[14]?.pressed ? 1 : 0);
        const dpadY = (pad.buttons[12]?.pressed ? 1 : 0) - (pad.buttons[13]?.pressed ? 1 : 0);
        if ((dpadX !== 0 || dpadY !== 0) && Math.hypot(mx, my) <= Config.player.deadzone) {
          const len = Math.hypot(dpadX, dpadY);
          mx = dpadX / len;
          my = dpadY / len;
        }
        ax = pad.axes[2] ?? 0;
        ay = -(pad.axes[3] ?? 0);
        const rt = pad.buttons[7];
        const rb = pad.buttons[5];
        const stick = Math.hypot(ax, ay);
        fire = (rt?.pressed ?? false) || (rb?.pressed ?? false) || stick > 0.5;
        // Wider than the movement deadzone so a drifting right stick can't hijack facing.
        aiming = stick > 0.25;
      }

      if (p === 0) {
        const touch = this.touchControls ? this.touchControls.getState() : null;
        const kx = (this.keys.has('KeyD') || this.keys.has('ArrowRight') ? 1 : 0) - (this.keys.has('KeyA') || this.keys.has('ArrowLeft') ? 1 : 0);
        const ky = (this.keys.has('KeyW') || this.keys.has('ArrowUp') ? 1 : 0) - (this.keys.has('KeyS') || this.keys.has('ArrowDown') ? 1 : 0);
        if (kx !== 0 || ky !== 0) {
          const len = Math.hypot(kx, ky);
          mx = kx / len;
          my = ky / len;
        } else if (touch && (touch.moveX !== 0 || touch.moveY !== 0)) {
          mx = touch.moveX;
          my = touch.moveY;
        }

        if (touch && (touch.aiming || touch.firing)) {
          ax = touch.aimX;
          ay = touch.aimY;
          aimIsPointer = false;
          aiming = true;
          fire = touch.firing;
        } else {
          if (Math.hypot(ax, ay) < Config.player.deadzone && (this.mouseMoved || this.mouseDown)) {
            aimIsPointer = true;
            // The pointer keeps supplying the aim vector, but only a recently moved (or pressed)
            // mouse counts as deliberate aiming; otherwise facing follows movement.
            if (this.mouseDown || this.pointerAge < POINTER_AIM_HOLD) aiming = true;
          }
          if (this.mouseDown || this.keys.has('Space')) fire = true;
        }
      }
      if (fire) aiming = true;

      input.moveX = mx;
      input.moveY = my;
      input.digital = this.digitalMask(p, mx, my, pad);
      input.fire = fire;
      input.aimIsPointer = aimIsPointer;
      input.aiming = aiming;
      if (!aimIsPointer) {
        const mag = Math.hypot(ax, ay);
        if (mag > Config.player.deadzone) {
          input.aimX = ax / mag;
          input.aimY = ay / mag;
        }
      }
    }
  }

  private applyMovement(eid: number, input: PlayerInput, dt: number): void {
    const c = Config.player;
    const dash = c.dash;
    const slot = Player.inputIndex[eid];
    this.lastTapAge[slot] += dt;
    if (this.dashCooldown[slot] > 0) this.dashCooldown[slot] -= dt;

    if (Player.dashTime[eid] > 0) {
      Player.dashTime[eid] -= dt;
      if (Player.dashTime[eid] > 0) {
        let speed = dash.speed;
        if (Player.swiftTime[eid] > 0) speed *= Config.pickups.swift.speedMult;
        const vx = Player.dashX[eid] * speed;
        const vy = Player.dashY[eid] * speed;
        Velocity.x[eid] = vx;
        Velocity.y[eid] = vy;
        Player.moveX[eid] = vx;
        Player.moveY[eid] = vy;
        Player.idleTime[eid] = 0;
        this.consumeDashTaps(slot, input.digital, -1);
        return;
      }
      Player.dashTime[eid] = 0;
    }

    const canDash = this.dashCooldown[slot] <= 0 && this.ctx.room.phase !== RoomPhase.Transition;
    if (canDash && this.touchControls?.consumeDash()) {
      const last = this.touchControls.lastMoveDir();
      const mx = Math.hypot(input.moveX, input.moveY) > Config.player.deadzone ? input.moveX : last.x;
      const my = Math.hypot(input.moveX, input.moveY) > Config.player.deadzone ? input.moveY : last.y;
      this.startDashVec(eid, mx, my);
      this.dashCooldown[slot] = dash.cooldown;
      return;
    }
    this.consumeDashTaps(slot, input.digital, canDash ? eid : -1);

    if (Player.dashTime[eid] > 0) return;

    let mag = Math.hypot(input.moveX, input.moveY);
    let tx = 0;
    let ty = 0;
    if (mag > c.deadzone) {
      const dirX = input.moveX / mag;
      const dirY = input.moveY / mag;
      if (mag > 1) mag = 1;
      const n = (mag - c.deadzone) / (1 - c.deadzone);
      let speed = c.moveSpeed * n * n;
      if (Player.swiftTime[eid] > 0) speed *= Config.pickups.swift.speedMult;
      const aimOpposes = dirX * Player.aimX[eid] + dirY * Player.aimY[eid] < -0.2;
      if (input.fire && aimOpposes) speed *= 1 - c.backpedalPenalty;
      tx = dirX * speed;
      ty = dirY * speed;
    }
    Player.moveX[eid] = tx;
    Player.moveY[eid] = ty;

    const vx = Velocity.x[eid];
    const vy = Velocity.y[eid];
    const speeding = tx * tx + ty * ty > vx * vx + vy * vy;
    const tau = speeding ? c.accelTime : c.slideTime;
    const k = 1 - Math.exp(-dt / tau);
    Velocity.x[eid] = vx + (tx - vx) * k;
    Velocity.y[eid] = vy + (ty - vy) * k;

    const moving = tx !== 0 || ty !== 0;
    Player.idleTime[eid] = moving ? 0 : Player.idleTime[eid] + dt;
  }

  /** Rising cardinal edges: second tap of the same bit inside the window starts a dash. */
  private consumeDashTaps(slot: number, digital: number, dashEid: number): void {
    const rose = digital & ~this.lastDigital[slot];
    this.lastDigital[slot] = digital;
    if (rose === 0 || dashEid < 0) return;
    const dash = Config.player.dash;
    const dir = this.pickRoseDir(rose, this.lastTapDir[slot]);
    if (dir !== 0 && dir === this.lastTapDir[slot] && this.lastTapAge[slot] <= dash.window) {
      this.startDash(dashEid, dir);
      this.lastTapDir[slot] = 0;
      this.lastTapAge[slot] = 99;
      this.dashCooldown[slot] = dash.cooldown;
      return;
    }
    this.lastTapDir[slot] = dir;
    this.lastTapAge[slot] = 0;
  }

  private startDash(eid: number, dir: number): void {
    const dx = dir === DIR_RIGHT ? 1 : dir === DIR_LEFT ? -1 : 0;
    const dy = dir === DIR_UP ? 1 : dir === DIR_DOWN ? -1 : 0;
    this.startDashVec(eid, dx, dy);
  }

  private startDashVec(eid: number, rawX: number, rawY: number): void {
    const dash = Config.player.dash;
    let dx = rawX;
    let dy = rawY;
    const len = Math.hypot(dx, dy);
    if (len < 0.001) {
      dx = Player.aimX[eid] || 1;
      dy = Player.aimY[eid] || 0;
    } else {
      dx /= len;
      dy /= len;
    }
    Player.dashTime[eid] = dash.duration;
    Player.dashX[eid] = dx;
    Player.dashY[eid] = dy;
    let speed = dash.speed;
    if (Player.swiftTime[eid] > 0) speed *= Config.pickups.swift.speedMult;
    Velocity.x[eid] = dx * speed;
    Velocity.y[eid] = dy * speed;
    Player.moveX[eid] = Velocity.x[eid];
    Player.moveY[eid] = Velocity.y[eid];
    Player.idleTime[eid] = 0;
    if (Health.invuln[eid] < dash.invuln) Health.invuln[eid] = dash.invuln;
    Sprite.squash[eid] = 0.42;
    Sprite.pop[eid] = Math.max(Sprite.pop[eid], 0.28);
    const x = Position.x[eid];
    const y = Position.y[eid];
    emitParticles(this.ctx.fx, ParticleKind.GateDust, x, y - 0.2, -dx, -dy, 0.9);
    emitParticles(this.ctx.fx, ParticleKind.Dust, x, y - 0.15, -dx, -dy, 0.55, 0xc8d4e0);
    this.ctx.events.push(GameEvent.PlayerDash, eid, 0, x, y);
  }

  private digitalMask(p: number, mx: number, my: number, pad: Gamepad | null): number {
    let mask = 0;
    if (p === 0) {
      if (this.keys.has('KeyD') || this.keys.has('ArrowRight')) mask |= DIR_RIGHT;
      if (this.keys.has('KeyA') || this.keys.has('ArrowLeft')) mask |= DIR_LEFT;
      if (this.keys.has('KeyW') || this.keys.has('ArrowUp')) mask |= DIR_UP;
      if (this.keys.has('KeyS') || this.keys.has('ArrowDown')) mask |= DIR_DOWN;
    }
    if (pad) {
      if (pad.buttons[15]?.pressed) mask |= DIR_RIGHT;
      if (pad.buttons[14]?.pressed) mask |= DIR_LEFT;
      if (pad.buttons[12]?.pressed) mask |= DIR_UP;
      if (pad.buttons[13]?.pressed) mask |= DIR_DOWN;
    }
    return mask | this.stickCardinal(mx, my);
  }

  private stickCardinal(x: number, y: number): number {
    const ax = Math.abs(x);
    const ay = Math.abs(y);
    const tap = Config.player.dash.stickTap;
    if (ax < tap && ay < tap) return 0;
    if (ax > ay * 1.35 && ax >= tap) return x > 0 ? DIR_RIGHT : DIR_LEFT;
    if (ay > ax * 1.35 && ay >= tap) return y > 0 ? DIR_UP : DIR_DOWN;
    return 0;
  }

  private pickRoseDir(rose: number, prefer: number): number {
    if (prefer && (rose & prefer)) return prefer;
    if (rose & DIR_RIGHT) return DIR_RIGHT;
    if (rose & DIR_LEFT) return DIR_LEFT;
    if (rose & DIR_UP) return DIR_UP;
    if (rose & DIR_DOWN) return DIR_DOWN;
    return 0;
  }

  private applyAim(eid: number, input: PlayerInput): void {
    let ax = input.aimX;
    let ay = input.aimY;
    if (input.aimIsPointer) {
      this.pointerToWorld(this.mouseX, this.mouseY, this.pointerWorld);
      const dx = this.pointerWorld[0] - Position.x[eid];
      const dy = this.pointerWorld[1] - Position.y[eid];
      const len = Math.hypot(dx, dy);
      if (len > 0.001) {
        ax = dx / len;
        ay = dy / len;
      }
    }

    // Aim magnetism: cling to the nearest live spawner within the cone.
    const spawners = query(this.ctx.world, [Spawner, Position]);
    let bestCos = AIM_MAGNET_COS;
    let bestX = 0;
    let bestY = 0;
    let found = false;
    for (let i = 0; i < spawners.length; i++) {
      const s = spawners[i];
      const dx = Position.x[s] - Position.x[eid];
      const dy = Position.y[s] - Position.y[eid];
      const len = Math.hypot(dx, dy);
      if (len < 0.01) continue;
      const cos = (dx * ax + dy * ay) / len;
      if (cos < bestCos) continue;
      bestCos = cos;
      bestX = dx / len;
      bestY = dy / len;
      found = true;
    }
    if (found) {
      ax = bestX;
      ay = bestY;
    }
    Player.aimX[eid] = ax;
    Player.aimY[eid] = ay;
  }

  private readonly onKeyDown = (e: KeyboardEvent): void => {
    this.keys.add(e.code);
    if (e.code === 'Space') e.preventDefault();
  };
  private readonly onKeyUp = (e: KeyboardEvent): void => {
    this.keys.delete(e.code);
  };
  private readonly onBlur = (): void => {
    this.keys.clear();
    this.mouseDown = false;
  };
  /** True when the event landed on the game canvas or the touch overlay above it. */
  private isGameplayMouseTarget(target: EventTarget | null): boolean {
    if (!(target instanceof Element)) return false;
    if (target === this.target || this.target.contains(target)) return true;
    return !!target.closest('.touch-controls');
  }

  private readonly onMouseMove = (e: MouseEvent): void => {
    if (!this.isGameplayMouseTarget(e.target)) return;
    this.mouseX = e.clientX;
    this.mouseY = e.clientY;
    this.mouseMoved = true;
    this.pointerAge = 0;
  };
  private readonly onMouseDown = (e: MouseEvent): void => {
    if (!this.isGameplayMouseTarget(e.target)) return;
    if (e.button === 0) this.mouseDown = true;
    this.mouseX = e.clientX;
    this.mouseY = e.clientY;
    this.mouseMoved = true;
    this.pointerAge = 0;
  };
  private readonly onMouseUp = (e: MouseEvent): void => {
    if (e.button === 0) this.mouseDown = false;
  };
  private readonly onContextMenu = (e: Event): void => {
    e.preventDefault();
  };
}
